// Pas d'import de formatDate : les consommateurs (Voting, DatePollResults)
// appellent formatDate eux-mêmes au rendu. Garder buildPollOptions sans
// dépendance UI permet de le tester isolément.

export interface BuiltPollOption {
  id: string;
  poll_id: string;
  title: string;
  label: string;
  description: string;
  option_date: string;
  option_text: string;
  time_label: string | null;
  date_group_label: string | null;
  time_slots: Array<{ hour: number; minute: number; duration: number }> | null;
  // Champs pour groupes de dates (week-ends, etc.)
  date_group?: string[];
  date_group_type?: "weekend" | "week" | "fortnight" | "custom";
}

interface RawTimeSlot {
  hour: number;
  minute: number;
  duration?: number;
  enabled?: boolean;
}

function getDayOfWeek(dateStr: string): number {
  const d = new Date(dateStr + "T00:00:00Z");
  return d.getUTCDay(); // 0=dimanche, 6=samedi
}

function formatWeekendLabel(saturdayStr: string, sundayStr: string): string {
  const satDate = new Date(saturdayStr + "T00:00:00Z");
  const sunDate = new Date(sundayStr + "T00:00:00Z");

  const dayMonths = [
    "jan",
    "fév",
    "mar",
    "avr",
    "mai",
    "juin",
    "juil",
    "aoû",
    "sep",
    "oct",
    "nov",
    "déc",
  ];
  const satDay = satDate.getUTCDate();
  const sunDay = sunDate.getUTCDate();
  const month = dayMonths[satDate.getUTCMonth()];

  return `Week-end du ${satDay}-${sunDay} ${month}`;
}

function isNextDay(dateStr1: string, dateStr2: string): boolean {
  const d1 = new Date(dateStr1 + "T00:00:00Z").getTime();
  const d2 = new Date(dateStr2 + "T00:00:00Z").getTime();
  const oneDayMs = 24 * 60 * 60 * 1000;
  return d2 - d1 === oneDayMs;
}

// Source unique pour Voting et Results : produit une option par créneau horaire
// activé, ou une option par date quand aucun créneau n'est défini. Garde l'IDs
// alignés ("1", "2", ...) avec ce que VotingSwipe / poll_responses utilisent.
export function buildPollOptions(realPoll: any): BuiltPollOption[] {
  if (!realPoll) return [];

  const settings = (realPoll as any).settings || {};
  const fallbackDuration: number =
    typeof settings.timeGranularity === "number" && settings.timeGranularity > 0
      ? settings.timeGranularity
      : 30;
  const mergeMode: boolean = settings.mergeMode === true;
  const weekendGroupingEnabled: boolean =
    settings.weekendGroupingEnabled === true;

  const selectedDates: string[] =
    (realPoll as any).selectedDates ||
    (realPoll as any).poll_data?.selectedDates ||
    (realPoll as any).poll_data?.dates ||
    [];
  const timeSlotsByDate: Record<string, RawTimeSlot[]> =
    (realPoll as any).timeSlotsByDate ||
    (realPoll as any).poll_data?.timeSlotsByDate ||
    {};

  const pollId: string = (realPoll as any).id || "";
  const options: BuiltPollOption[] = [];
  let index = 0;

  // Track which dates have been processed (for weekend grouping)
  const processedDates = new Set<string>();

  selectedDates.forEach((date: string, dateIndex: number) => {
    // Skip if already processed as part of a group
    if (processedDates.has(date)) return;

    const slots = timeSlotsByDate[date] || [];
    // Sort enabled slots chronologically
    const enabled = slots
      .filter((s) => s.enabled)
      .sort((a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute));

    // Check for weekend grouping: samedi (5) + dimanche (6) pair
    const shouldGroupWithNextDate =
      weekendGroupingEnabled &&
      enabled.length === 0 && // Only group when no time slots
      dateIndex + 1 < selectedDates.length;

    const nextDate = shouldGroupWithNextDate
      ? selectedDates[dateIndex + 1]
      : null;
    const isDayBeforeNextDate = nextDate && isNextDay(date, nextDate);
    const isSaturday = getDayOfWeek(date) === 6;
    const nextIsSunday = nextDate && getDayOfWeek(nextDate) === 0;

    if (
      shouldGroupWithNextDate &&
      isDayBeforeNextDate &&
      isSaturday &&
      nextIsSunday
    ) {
      // Create grouped option for weekend
      const groupLabel = formatWeekendLabel(date, nextDate);
      options.push({
        id: (++index).toString(),
        poll_id: pollId,
        title: date,
        label: groupLabel,
        description: "",
        option_date: date,
        option_text: groupLabel,
        time_label: null,
        date_group_label: groupLabel,
        time_slots: null,
        date_group: [date, nextDate],
        date_group_type: "weekend",
      });
      processedDates.add(date);
      processedDates.add(nextDate);
      return;
    }

    processedDates.add(date);

    if (enabled.length === 0) {
      options.push({
        id: (++index).toString(),
        poll_id: pollId,
        title: date,
        label: date,
        description: "",
        option_date: date,
        option_text: date,
        time_label: null,
        date_group_label: null,
        time_slots: null,
        date_group: undefined,
        date_group_type: undefined,
      });
      return;
    }

    // Build blocks: merge consecutive slots if mergeMode, else one block per slot
    type Block = {
      startHour: number;
      startMinute: number;
      durationMinutes: number;
    };
    const blocks: Block[] = [];

    for (const slot of enabled) {
      const slotDur =
        slot.duration != null && slot.duration > 0
          ? slot.duration
          : fallbackDuration;
      const slotStartAbs = slot.hour * 60 + slot.minute;

      if (mergeMode && blocks.length > 0) {
        const last = blocks[blocks.length - 1];
        const lastEndAbs =
          last.startHour * 60 + last.startMinute + last.durationMinutes;
        if (lastEndAbs === slotStartAbs) {
          // Consecutive in merge mode → extend current block
          last.durationMinutes += slotDur;
          continue;
        }
      }
      blocks.push({
        startHour: slot.hour,
        startMinute: slot.minute,
        durationMinutes: slotDur,
      });
    }

    blocks.forEach((block) => {
      const duration = block.durationMinutes;
      const startTime = `${block.startHour.toString().padStart(2, "0")}:${block.startMinute.toString().padStart(2, "0")}`;
      const endTotalMinutes =
        block.startHour * 60 + block.startMinute + duration;
      const endHour = Math.floor(endTotalMinutes / 60);
      const endMinute = endTotalMinutes % 60;
      const endTime = `${endHour.toString().padStart(2, "0")}:${endMinute.toString().padStart(2, "0")}`;
      const timeLabel = `${startTime} - ${endTime}`;

      options.push({
        id: (++index).toString(),
        poll_id: pollId,
        title: date,
        label: `${date} ${timeLabel}`,
        description: timeLabel,
        option_date: date,
        option_text: `${date} ${timeLabel}`,
        time_label: timeLabel,
        date_group_label: null,
        time_slots: [
          { hour: block.startHour, minute: block.startMinute, duration },
        ],
        date_group: undefined,
        date_group_type: undefined,
      });
    });
  });

  return options;
}
